import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminSubscriptionPlans from './AdminSubscriptionPlans';
import UserManagement from './UserManagement';
import ErrorLogs from './ErrorLogs';
import FrontendErrorLogs from './FrontendErrorLogs';
import BenchmarkDashboard from './BenchmarkDashboard';
import TuneSearch from './TuneSearch';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active tab from URL
  const getActiveTab = useCallback(() => {
    if (location.pathname.includes('/admin/subscription-plans')) {
      return 'subscription-plans';
    }
    if (location.pathname.includes('/admin/user-management')) {
      return 'user-management';
    }
    if (location.pathname.includes('/admin/frontend-errors')) {
      return 'frontend-errors';
    }
    if (location.pathname.includes('/admin/error-logs')) {
      return 'error-logs';
    }
    if (location.pathname.includes('/admin/benchmarks')) {
      return 'benchmarks';
    }
    if (location.pathname.includes('/admin/tune-search')) {
      return 'tune-search';
    }
    return 'subscription-plans'; // default
  }, [location.pathname]);
  
  const [activeTab, setActiveTab] = useState<string>(getActiveTab());

  // Sync tab with URL changes
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [getActiveTab]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === 'subscription-plans') {
      navigate('/admin/subscription-plans');
    } else if (value === 'user-management') {
      navigate('/admin/user-management');
    } else if (value === 'error-logs') {
      navigate('/admin/error-logs');
    } else if (value === 'frontend-errors') {
      navigate('/admin/frontend-errors');
    } else if (value === 'benchmarks') {
      navigate('/admin/benchmarks');
    } else if (value === 'tune-search') {
      navigate('/admin/tune-search');
    }
  };

  return (
    <div className="w-full p-2 sm:p-4">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Admin Panel</h1>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1 sm:gap-0 max-w-5xl h-auto p-1">
          <TabsTrigger value="subscription-plans" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Subscription Plans
          </TabsTrigger>
          <TabsTrigger value="user-management" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            User Management
          </TabsTrigger>
          <TabsTrigger value="error-logs" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Server Errors
          </TabsTrigger>
          <TabsTrigger value="frontend-errors" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Frontend Errors
          </TabsTrigger>
          <TabsTrigger value="benchmarks" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Benchmarks
          </TabsTrigger>
          <TabsTrigger value="tune-search" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Tune Search
          </TabsTrigger>
        </TabsList>

        <TabsContent value="subscription-plans" className="mt-4 sm:mt-6">
          <AdminSubscriptionPlans />
        </TabsContent>

        <TabsContent value="user-management" className="mt-4 sm:mt-6">
          <UserManagement />
        </TabsContent>

        <TabsContent value="error-logs" className="mt-4 sm:mt-6">
          <ErrorLogs />
        </TabsContent>

        <TabsContent value="frontend-errors" className="mt-4 sm:mt-6">
          <FrontendErrorLogs />
        </TabsContent>

        <TabsContent value="benchmarks" className="mt-4 sm:mt-6">
          <BenchmarkDashboard />
        </TabsContent>

        <TabsContent value="tune-search" className="mt-4 sm:mt-6">
          <TuneSearch />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
